import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PROFILE_DIAGNOSTICS } from "../../profiles";
import { filterProfiles } from "../upload/profile-picker-filter";
import { DraftProfileDetail } from "./DraftProfileDetail";
import type { FleetRow } from "./fleet";
import { HelpButton } from "../help/HelpDrawer";
import { ProfileDetail } from "./ProfileDetail";
import { useFleet } from "./use-fleet";

/**
 * Master-detail over the fleet: search on the left, resolved policy and the
 * local-override lifecycle on the right. Deep-linkable as /profiles?id=<id>.
 *
 * Each row carries what a person actually asks of a fleet list — when this
 * source was last analysed here, how it came out, and whose policy governed it
 * — so the answer does not require opening thirteen profiles in turn.
 */
export function ProfilesPage() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const { rows, counts } = useFleet();
  const matches = useMemo(() => filterProfiles(rows, query), [rows, query]);
  const selectedId = params.get("id") ?? matches[0]?.id ?? null;
  const selected = rows.find((row) => row.id === selectedId) ?? null;

  return (
    <main className="grid gap-4 p-6 md:grid-cols-[320px_1fr]" data-testid="profiles-page">
      <aside className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">Profiles ({counts.committed})</h2>
          <HelpButton topicId="profiles" label="Help for profiles" />
        </div>
        <p className="text-xs text-slate-600" data-testid="fleet-counts">
          {counts.analysed} analysed here
          {counts.drafts > 0 ? ` · ${counts.drafts} draft${counts.drafts === 1 ? "" : "s"}` : ""}
          {counts.overridden > 0 ? ` · ${counts.overridden} overridden` : ""}
          {counts.staleOverrides > 0 ? ` · ${counts.staleOverrides} stale` : ""}
        </p>
        <input
          className="w-full rounded-sm border border-slate-300 p-2 text-sm"
          data-testid="profiles-search"
          placeholder="Search by name, id, or URL"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <div className="max-h-[70vh] overflow-y-auto rounded-sm border bg-white">
          {matches.length === 0 ? (
            <p className="p-3 text-sm text-slate-500">No profiles match.</p>
          ) : (
            matches.map((row) => (
              <button
                key={row.id}
                type="button"
                data-testid={`profiles-row-${row.id}`}
                className={`block w-full border-b px-3 py-2 text-left text-sm last:border-b-0 ${
                  row.id === selectedId ? "bg-sky-50" : "bg-white hover:bg-slate-50"
                }`}
                onClick={() => setParams({ id: row.id })}
              >
                <span className="block truncate font-medium">{row.displayName}</span>
                <span className="block truncate text-xs text-slate-600">
                  {row.id}
                  {row.version === undefined ? "" : ` · v${row.version}`}
                </span>
                <FleetRowFacts row={row} />
              </button>
            ))
          )}
        </div>
        {PROFILE_DIAGNOSTICS.length > 0 ? (
          <div className="rounded-sm border border-red-300 bg-red-50 p-2 text-xs text-red-700" data-testid="profile-diagnostics">
            <p className="font-semibold">{PROFILE_DIAGNOSTICS.length} profile file(s) failed to load:</p>
            <ul className="list-inside list-disc">
              {PROFILE_DIAGNOSTICS.map((issue) => (
                <li key={issue.file}>
                  {issue.file}: {issue.problems.join(" ")}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </aside>
      <section className="rounded-sm border bg-slate-50/50">
        {selected?.kind === "draft" ? (
          <DraftProfileDetail key={selected.id} row={selected} />
        ) : selectedId ? (
          <ProfileDetail key={selectedId} profileId={selectedId} />
        ) : (
          <p className="p-4 text-sm text-slate-500">Select a profile.</p>
        )}
      </section>
    </main>
  );
}

/**
 * The per-row fleet facts.
 *
 * "Not analysed here" is stated rather than left blank: an empty line reads as
 * a clean run to anyone scanning the list, and the analysis cache is bounded,
 * so absence means "no cached run", never "no run ever".
 */
function FleetRowFacts({ row }: { row: FleetRow }) {
  return (
    <span className="mt-1 flex flex-wrap items-center gap-1 text-xs" data-testid={`fleet-facts-${row.id}`}>
      {row.kind === "draft" ? <Badge tone="amber">draft</Badge> : null}
      {row.override?.status === "active" ? <Badge tone="sky">override r{row.override.revision}</Badge> : null}
      {row.override?.status === "stale" ? <Badge tone="slate">override stale</Badge> : null}
      {row.lastVerdict === null ? (
        <span className="text-slate-500">Not analysed here</span>
      ) : (
        <>
          <Badge tone={verdictTone(row.lastVerdict)}>{row.lastVerdict}</Badge>
          <span className="text-slate-600">{formatDay(row.lastAnalyzedAt!)}</span>
        </>
      )}
    </span>
  );
}

function verdictTone(verdict: NonNullable<FleetRow["lastVerdict"]>): BadgeTone {
  if (verdict === "Quarantined") return "red";
  if (verdict === "Warning") return "amber";
  return "green";
}

type BadgeTone = "green" | "amber" | "red" | "sky" | "slate";

const TONES: Record<BadgeTone, string> = {
  green: "border-green-300 bg-green-50 text-green-800",
  amber: "border-amber-300 bg-amber-50 text-amber-800",
  red: "border-red-300 bg-red-50 text-red-800",
  sky: "border-sky-300 bg-sky-50 text-sky-800",
  slate: "border-slate-300 bg-slate-100 text-slate-700"
};

function Badge({ tone, children }: { tone: BadgeTone; children: React.ReactNode }) {
  return <span className={`rounded-sm border px-1.5 py-0.5 font-medium ${TONES[tone]}`}>{children}</span>;
}

/** Day precision in the list; the detail pane carries the full timestamp. */
function formatDay(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : at.toLocaleDateString();
}
